import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import CloudWatchLogger from 'services/cloudwatch/cloudwatchLogger';
import { useCognito } from 'common/hooks/useCognito';
import { useAuth } from './AuthContext';

interface LoggingContextType {
    logger: CloudWatchLogger;
    info: (message: string, metadata?: Record<string, any>, component?: string, action?: string) => void;
    warn: (message: string, metadata?: Record<string, any>, component?: string, action?: string) => void;
    error: (
        message: string,
        error?: Error,
        metadata?: Record<string, any>,
        component?: string,
        action?: string
    ) => void;
    setUserId: (userId: string) => void;
}

const LoggingContext = createContext<LoggingContextType | undefined>(undefined);

interface LoggingProviderProps {
    children: ReactNode;
}

export const LoggingProvider: React.FC<LoggingProviderProps> = ({ children }) => {
    const loggerRef = useRef<CloudWatchLogger | null>(null);
    const { getTokenId } = useCognito();
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        if (loggerRef.current || !isAuthenticated) return;

        getTokenId({
            onSuccess: async (idToken: string) => {
                try {
                    loggerRef.current = CloudWatchLogger.getInstance();
                    await loggerRef.current?.initializeClient(idToken);

                    loggerRef.current?.info(
                        'Application started',
                        {
                            userAgent: navigator.userAgent,
                            url: window.location.href,
                            timestamp: new Date().toISOString(),
                        },
                        'App',
                        'startup'
                    );
                } catch (error) {
                    console.error('Failed to initialize CloudWatch Logger after authentication:', error);
                }
            },
            onFailure: (error: any) => {
                console.log('Failed to get ID token for CloudWatch initialization:', error);
            },
        });

        // Cleanup on unmount
        return () => {
            if (loggerRef.current) {
                loggerRef.current.info('Application shutting down', {}, 'App', 'shutdown');
                loggerRef.current.destroy();
            }
        };
    }, [getTokenId, isAuthenticated]);

    // Handle page unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (loggerRef.current) {
                loggerRef.current.flush();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // Handle visibility change (tab switching)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (loggerRef.current) {
                if (document.hidden) {
                    loggerRef.current.info('Tab became hidden', {}, 'App', 'tab_hidden');
                    loggerRef.current.flush();
                } else {
                    loggerRef.current.info('Tab became visible', {}, 'App', 'tab_visible');
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const contextValue: LoggingContextType = {
        logger: loggerRef.current as CloudWatchLogger,
        info: (message, metadata, component, action) => {
            loggerRef.current?.info(message, metadata, component, action);
        },
        warn: (message, metadata, component, action) => {
            loggerRef.current?.warn(message, metadata, component, action);
        },
        error: (message, error, metadata, component, action) => {
            loggerRef.current?.error(message, error, metadata, component, action);
        },
        setUserId: (userId) => {
            loggerRef.current?.setUserId(userId);
        },
    };

    return <LoggingContext.Provider value={contextValue}>{children}</LoggingContext.Provider>;
};

// Custom hook to use logging
export const useLogging = (): LoggingContextType => {
    const context = useContext(LoggingContext);
    if (context === undefined) {
        throw new Error('useLogging must be used within a LoggingProvider');
    }
    return context;
};

// HOC for automatic component logging
export const withLogging = <P extends object>(WrappedComponent: React.ComponentType<P>, componentName?: string) => {
    const WithLoggingComponent = (props: P) => {
        const { info, error } = useLogging();
        const displayName = componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component';

        useEffect(() => {
            info(`${displayName} mounted`, {}, displayName, 'mount');

            return () => {
                info(`${displayName} unmounted`, {}, displayName, 'unmount');
            };
        }, [info, displayName]);

        try {
            return <WrappedComponent {...props} />;
        } catch (err) {
            error(`Error in ${displayName}`, err as Error, {}, displayName, 'render_error');
            throw err;
        }
    };

    WithLoggingComponent.displayName = `withLogging(${componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return WithLoggingComponent;
};
